import type { OS } from '../hooks/useOS';
import { startCase } from 'lodash';

/**
 * Safely parses a JSON string and returns the parsed object or null if parsing fails
 * @param jsonString - The JSON string to parse
 * @param fallback - Optional fallback value to return if parsing fails (defaults to null)
 * @returns The parsed object or the fallback value
 */
export const safeParseJson = <T = unknown>(
  jsonString: string | null | undefined,
  fallback: T | null = null,
): T | null => {
  if (!jsonString) {
    return fallback;
  }

  try {
    return JSON.parse(jsonString) as T;
  } catch (error) {
    console.error('Failed to parse JSON:', error);
    return fallback;
  }
};

const keyMap: Record<string, { mac: string; others: string }> = {
  shift: { mac: '⇧', others: '⇧' },
  alt: { mac: '⌥', others: 'Alt' },
  ctrl: { mac: 'Ctrl', others: 'Ctrl' },
  meta: { mac: '⌘', others: '⊞' },
  mod: { mac: '⌘', others: 'Ctrl' },
  backspace: { mac: '⌫', others: '⌫' },
  tab: { mac: '⇥', others: '⇥' },
  enter: { mac: '↩', others: '↩' },
  return: { mac: '↩', others: '↩' },
  esc: { mac: 'Esc', others: 'Esc' },
  escape: { mac: 'Esc', others: 'Esc' },
  space: { mac: '␣', others: '␣' },
  up: { mac: '↑', others: '↑' },
  down: { mac: '↓', others: '↓' },
  left: { mac: '←', others: '←' },
  right: { mac: '→', others: '→' },
  pageup: { mac: 'PageUp', others: 'PageUp' },
  pagedown: { mac: 'PageDown', others: 'PageDown' },
  delete: { mac: '⌦', others: '⌦' },
  del: { mac: '⌦', others: '⌦' },
};

export function hotkeyToSymbol(hotkey: string, platform: OS = 'mac'): string {
  const keys = hotkey.toLowerCase().split('+');

  return keys
    .map(key => {
      const mapped = keyMap[key.trim()];
      return mapped
        ? mapped[platform === 'mac' ? 'mac' : 'others']
        : key.toUpperCase();
    })
    .join('');
}

/**
 * Truncates a string to a maximum length and adds ellipsis if truncated
 * @param str - The string to truncate
 * @param maxLength - Maximum length before truncation (null/undefined means no truncation)
 * @param ellipsis - The string to append when truncated (defaults to '...')
 * @returns The original string if under max length, or truncated string with ellipsis
 */
export function truncateString(
  str: string,
  maxLength: number | null | undefined,
  ellipsis = '...',
): string {
  // No truncation if maxLength is not provided or string is within limit
  if (maxLength == null || str.length <= maxLength) {
    return str;
  }

  // Truncate and add ellipsis
  return str.substring(0, maxLength) + ellipsis;
}

/**
 * Gets the first letter of a string capitalized
 * @param str - The string to get the initial from
 * @returns The first letter capitalized, or empty string if str is empty/undefined
 */
export function getInitial(str: string | undefined | null): string {
  const trimmedStr = str?.trim();
  if (!trimmedStr) {
    return '';
  }
  return trimmedStr.charAt(0).toUpperCase();
}

/**
 * Formats a string by replacing underscores with spaces and capitalizing each word
 * Useful for converting snake_case names to human-readable display names
 * Uses lodash startCase for consistent formatting
 * @param str - The string to format
 * @returns The formatted string with spaces and capitalized words
 * @example
 * formatDisplayName('user_first_name') // 'User First Name'
 */
export function formatDisplayName(str: string): string {
  return startCase(str);
}

/**
 * Converts a value to a string for input display.
 * Handles null/undefined values by returning an empty string.
 *
 * Useful for form input values where null/undefined should display as empty.
 *
 * @param value - The value to convert
 * @returns The string representation of the value, or empty string if null/undefined
 * @example
 * toInputValue('hello') // 'hello'
 * toInputValue(123) // '123'
 * toInputValue(null) // ''
 * toInputValue(undefined) // ''
 */
export function toInputValue(value: unknown): string {
  return value != null ? String(value) : '';
}
